// @ts-nocheck - Temporary fix for push notification service types
import type { PushNotificationPayload, LocationData } from '@/types';

/**
 * Push Notification Service
 * 
 * Handles browser push notifications with graceful fallbacks for:
 * - Incognito mode (Chrome limitation - no Push API support)
 * - Unsupported browsers
 * - Permission denied scenarios
 * 
 * All errors are handled gracefully to prevent app disruption.
 */

interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

class PushNotificationService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey = 'BEl62iUYgUivxIkv69yViEuiBIa40HcCWLdHw0h4NjwelzToxG6omgK6dwdnQ9VqZHzGJWBqaBgtHHfcBJbkBNw'; // Demo key
  private initialized = false;

  async initialize(): Promise<boolean> {
    if (this.initialized) {
      return this.swRegistration !== null;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('⚠️ Push notifications not supported in this browser');
      return false;
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('✅ Service Worker registered:', this.swRegistration);
      this.initialized = true;
      
      // Request notification permission
      const permission = await this.requestPermission();
      return permission === 'granted';
    } catch (error: any) {
      // Handle incognito mode and other browser limitations gracefully
      if (error.message?.includes('incognito') || error.name === 'AbortError') {
        console.warn('⚠️ Push notifications not available (likely incognito mode)');
      } else {
        console.error('❌ Failed to initialize push notifications:', error);
      }
      return false;
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  async subscribe(userId: string): Promise<PushSubscription | null> {
    if (!this.swRegistration) {
      console.error('Service worker not registered');
      return null;
    }

    try {
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      const pushSubscription: PushSubscription = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
        }
      };

      // Store subscription on server (mock for now)
      localStorage.setItem(`push_subscription_${userId}`, JSON.stringify(pushSubscription));
      
      return pushSubscription;
    } catch (error: any) {
      // Handle specific browser limitations gracefully
      if (error.name === 'AbortError' || error.message?.includes('Registration failed')) {
        console.warn('⚠️ Push notifications not available (likely incognito mode or browser restriction)');
      } else if (error.name === 'NotSupportedError') {
        console.warn('⚠️ Push notifications not supported in this browser');
      } else {
        console.error('❌ Failed to subscribe to push notifications:', error);
      }
      return null;
    }
  }

  async unsubscribe(userId: string): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        localStorage.removeItem(`push_subscription_${userId}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  // Send local notification (for demo purposes)
  async sendLocalNotification(title: string, options: NotificationOptions = {}): Promise<void> {
    if (Notification.permission !== 'granted') {
      return;
    }

    const notification = new Notification(title, {
      icon: '/icon-192x192.png',
      badge: '/badge-72x72.png',
      vibrate: [200, 100, 200],
      ...options
    });

    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.data?.url) {
        window.location.href = options.data.url;
      }
    };
  }

  // Send push notification with location data
  async sendNotification(userId: string, payload: PushNotificationPayload): Promise<void> {
    const subscription = localStorage.getItem(`push_subscription_${userId}`);
    if (!subscription) {
      console.warn('No push subscription found for user:', userId);
      return;
    }

    // In a real app, this would send to your push service
    // For demo, we'll show a local notification
    await this.sendLocalNotification(payload.title, {
      body: payload.body,
      icon: payload.icon,
      badge: payload.badge,
      data: payload.data,
      vibrate: payload.vibrate,
      requireInteraction: payload.requireInteraction,
      silent: payload.silent
    });
  }

  // Enhanced notification with location context
  async sendLocationBasedNotification(
    userId: string, 
    payload: PushNotificationPayload, 
    location?: LocationData
  ): Promise<void> {
    const enhancedPayload = {
      ...payload,
      data: {
        ...payload.data,
        location: location ? {
          lat: location.latitude,
          lng: location.longitude,
          address: location.address,
          timestamp: location.timestamp.toISOString()
        } : undefined
      }
    };

    await this.sendNotification(userId, enhancedPayload);
  }

  // Job lifecycle notifications
  async sendJobLifecycleNotification(
    userId: string,
    type: 'job_started' | 'milestone_completed' | 'job_completed' | 'quality_check' | 'warranty_reminder',
    jobData: any,
    location?: LocationData
  ): Promise<void> {
    const notifications = {
      job_started: {
        title: '🚀 Work Started!',
        body: `${jobData.title} has been started by your provider.`,
        icon: '/icon-192x192.png',
        data: { jobId: jobData.id, type: 'job_update' }
      },
      milestone_completed: {
        title: '✅ Milestone Completed',
        body: `"${jobData.milestoneTitle}" completed for ${jobData.title}`,
        icon: '/icon-192x192.png',
        data: { jobId: jobData.id, milestoneId: jobData.milestoneId, type: 'milestone_update' }
      },
      job_completed: {
        title: '🎉 Job Completed!',
        body: `${jobData.title} has been completed. Please review the work.`,
        icon: '/icon-192x192.png',
        data: { jobId: jobData.id, type: 'job_completed' },
        requireInteraction: true
      },
      quality_check: {
        title: '📋 Quality Check',
        body: `How was the quality of work for "${jobData.title}"?`,
        icon: '/icon-192x192.png',
        data: { jobId: jobData.id, type: 'quality_check' }
      },
      warranty_reminder: {
        title: '🛡️ Warranty Reminder',
        body: `Your warranty for "${jobData.title}" expires soon`,
        icon: '/icon-192x192.png',
        data: { jobId: jobData.id, type: 'warranty_reminder' }
      }
    };

    const notification = notifications[type];
    await this.sendLocationBasedNotification(userId, notification, location);
  }

  // Simulate push notifications for demo
  async simulatePushNotification(type: 'quote' | 'message' | 'booking', data: any): Promise<void> {
    const notifications = {
      quote: {
        title: 'New Quote Received!',
        body: `You received a quote of €${data.amount} for "${data.jobTitle}"`,
        icon: '/icon-192x192.png',
        data: { url: `/jobs/${data.jobId}` }
      },
      message: {
        title: 'New Message',
        body: `${data.senderName}: ${data.message.substring(0, 50)}...`,
        icon: '/icon-192x192.png',
        data: { url: `/chat/${data.chatId}` }
      },
      booking: {
        title: 'Booking Confirmed!',
        body: `Your booking with ${data.providerName} has been confirmed`,
        icon: '/icon-192x192.png',
        data: { url: `/jobs/${data.jobId}` }
      }
    };

    const notification = notifications[type];
    await this.sendLocalNotification(notification.title, {
      body: notification.body,
      icon: notification.icon,
      data: notification.data,
      tag: `${type}-${data.id || Date.now()}`,
      requireInteraction: true
    });
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

export const pushNotificationService = new PushNotificationService();